import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import apiClient from '../config/axios';
import toast from 'react-hot-toast';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { ArrowLeft, Save, Settings, Calendar, Mail, Loader2 } from 'lucide-react';

// Helper functions for timezone conversion
// Convert local datetime-local value to UTC ISO string
// IMPORTANT: datetime-local input values are ALWAYS in the user's local timezone
// This function correctly converts them to UTC for database storage
const localTimeToUTC = (localDateTimeString) => {
    if (!localDateTimeString) return '';

    // datetime-local format: "2026-01-12T19:55" (no timezone info, always local)
    // When we create a Date object from this string, JavaScript interprets it as LOCAL time
    // We need to ensure it's treated as local time and then converted to UTC

    // Parse the datetime-local string (format: YYYY-MM-DDTHH:mm)
    const [datePart, timePart] = localDateTimeString.split('T');
    if (!datePart || !timePart) {
        console.error('Invalid datetime-local format:', localDateTimeString);
        return '';
    }

    const [year, month, day] = datePart.split('-').map(Number);
    const [hours, minutes] = timePart.split(':').map(Number);

    // Create a Date object in LOCAL timezone (this is what datetime-local represents)
    // Using new Date(year, month-1, day, hours, minutes) creates a date in LOCAL timezone
    const localDate = new Date(year, month - 1, day, hours, minutes, 0, 0);

    // Check if date is valid
    if (isNaN(localDate.getTime())) {
        console.error('Invalid date created from:', localDateTimeString);
        return '';
    }

    // Convert to UTC ISO string (this automatically handles timezone conversion)
    const utcISOString = localDate.toISOString();

    console.log('🕐 Email campaign time conversion:', {
        input: localDateTimeString,
        localDate: localDate.toString(),
        utcISO: utcISOString,
        localHours: hours,
        localMinutes: minutes,
        utcHours: localDate.getUTCHours(),
        utcMinutes: localDate.getUTCMinutes()
    });

    return utcISOString;
};

// Convert UTC ISO string to local datetime-local format
const utcToLocalTime = (utcISOString) => {
    if (!utcISOString) return '';
    // UTC ISO format: "2026-01-12T14:25:00.000Z"
    // Create Date object from UTC
    const utcDate = new Date(utcISOString);
    // Get local time components
    const year = utcDate.getFullYear();
    const month = String(utcDate.getMonth() + 1).padStart(2, '0');
    const day = String(utcDate.getDate()).padStart(2, '0');
    const hours = String(utcDate.getHours()).padStart(2, '0');
    const minutes = String(utcDate.getMinutes()).padStart(2, '0');
    // Return in datetime-local format: "2026-01-12T19:55"
    return `${year}-${month}-${day}T${hours}:${minutes}`;
};

// Helper function to generate cron expression
// Converts local time to UTC for consistent scheduling across timezones
const generateCronExpression = (frequency, time, daysOfWeek = [], dayOfMonth = 1) => {
    const [hours, minutes] = time.split(':').map(Number);

    // Create a date object with the user's local time (today)
    const localDate = new Date();
    localDate.setHours(hours, minutes, 0, 0);

    // Convert to UTC
    const utcHours = localDate.getUTCHours();
    const utcMinutes = localDate.getUTCMinutes();

    switch (frequency) {
        case 'daily':
            return `${utcMinutes} ${utcHours} * * *`; // Every day at specified time (UTC)
        case 'weekly':
            if (daysOfWeek.length === 0) {
                return `${utcMinutes} ${utcHours} * * 1`; // Default to Monday if no days selected
            }
            // For multiple days, return comma-separated weekdays
            const weekdayStr = daysOfWeek.join(',');
            return `${utcMinutes} ${utcHours} * * ${weekdayStr}`;
        case 'monthly':
            return `${utcMinutes} ${utcHours} ${dayOfMonth} * *`; // Specific day of month at specified time (UTC)
        default:
            return '0 9 * * *'; // Default: Daily at 9 AM UTC
    }
};

// Helper function to format cron to readable text
const formatCronToReadable = (cronExpr) => {
    const parts = cronExpr.split(' ');
    if (parts.length !== 5) return cronExpr;

    const [min, hour, day, month, weekday] = parts;
    const timeStr = `${parseInt(hour).toString().padStart(2, '0')}:${min.padStart(2, '0')}`;

    // Daily pattern: * * * *
    if (day === '*' && month === '*' && weekday === '*') {
        return `Every day at ${timeStr}`;
    }

    // Weekly pattern: * * * DAY or * * * DAY1,DAY2,...
    if (day === '*' && month === '*' && weekday !== '*') {
        const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

        // Check if multiple days (comma-separated)
        if (weekday.includes(',')) {
            const dayNumbers = weekday.split(',').map(d => parseInt(d));
            const dayNames = dayNumbers.map(d => days[d]);
            return `Every ${dayNames.join(', ')} at ${timeStr}`;
        }

        return `Every ${days[parseInt(weekday)]} at ${timeStr}`;
    }

    // Monthly pattern: * * DAY * *
    if (day !== '*' && month === '*' && weekday === '*') {
        const dayNum = parseInt(day);
        const daySuffix = getDaySuffix(dayNum);
        return `Every month on the ${dayNum}${daySuffix} at ${timeStr}`;
    }

    return cronExpr;
};

// Helper function to get day suffix (st, nd, rd, th)
const getDaySuffix = (day) => {
    if (day >= 11 && day <= 13) return 'th';
    switch (day % 10) {
        case 1: return 'st';
        case 2: return 'nd';
        case 3: return 'rd';
        default: return 'th';
    }
};

const CreateEmailCampaign = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const { currentWorkspace } = useWorkspace();
    const queryClient = useQueryClient();
    const isEditMode = !!id;

    const [formData, setFormData] = useState({
        name: '',
        description: '',
        email_template_id: '',
        schedule_type: 'once',
        schedule_time: '',
        cron_expr: '0 9 * * *',
        recurring_config: {
            frequency: 'daily',
            time: '09:00',
            daysOfWeek: [],
            dayOfMonth: 1
        },
        audience: {
            type: 'groups', // 'groups' or 'tags'
            include_groups: [],
            include_tags: []
        },
        smtp_id: '',
        email_delay_seconds: 1 // Delay between emails in seconds
    });

    const [templates, setTemplates] = useState([]);
    const [groups, setGroups] = useState([]);
    const [tags, setTags] = useState([]);
    const [smtpList, setSmtpList] = useState([]);
    const [totalContacts, setTotalContacts] = useState(0);
    const [totalContactsWithEmail, setTotalContactsWithEmail] = useState(0);
    const [templatePreview, setTemplatePreview] = useState(null);
    const [templatePreviewLoading, setTemplatePreviewLoading] = useState(false);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    // Fetch templates
    useEffect(() => {
        const fetchTemplates = async () => {
            try {
                const headers = currentWorkspace?.id ? { 'x-workspace-id': currentWorkspace.id } : {};
                const response = await apiClient.get('/api/email-templates', { headers });
                console.log('📧 Templates response:', response.data);
                setTemplates(response.data.templates || []);
            } catch (error) {
                console.error('Error fetching templates:', error);
                toast.error('Failed to load email templates');
            }
        };
        fetchTemplates();
    }, [currentWorkspace]);

    // Fetch groups
    useEffect(() => {
        const fetchGroups = async () => {
            try {
                const headers = currentWorkspace?.id ? { 'x-workspace-id': currentWorkspace.id } : {};
                const response = await apiClient.get('/api/contacts/groups/list', {
                    headers,
                    params: { _t: Date.now() }
                });
                const list = (response.data.groups || []).map((g) => ({
                    ...g,
                    count: Number(g.count) || 0,
                    email_count: Number(g.email_count) || 0
                }));
                console.log('📋 Groups response:', list);
                setGroups(list);
            } catch (error) {
                console.error('Error fetching groups:', error);
                toast.error('Failed to load groups');
            }
        };
        fetchGroups();
    }, [currentWorkspace]);

    // Fetch SMTP configurations
    useEffect(() => {
        const fetchSMTPList = async () => {
            try {
                const headers = currentWorkspace?.id ? { 'x-workspace-id': currentWorkspace.id } : {};
                const response = await apiClient.get('/api/settings/smtp', { headers });
                console.log('📧 SMTP response:', response.data);
                setSmtpList(response.data.smtp_list || []);
            } catch (error) {
                console.error('Error fetching SMTP configurations:', error);
                toast.error('Failed to load SMTP configurations');
            }
        };
        fetchSMTPList();
    }, [currentWorkspace]);

    // Fetch tags
    useEffect(() => {
        const fetchTags = async () => {
            try {
                const headers = currentWorkspace?.id ? { 'x-workspace-id': currentWorkspace.id } : {};
                const response = await apiClient.get('/api/contacts/tags/list', { headers });
                console.log('🏷️ Tags response:', response.data);
                setTags(response.data.tags || []);
            } catch (error) {
                console.error('Error fetching tags:', error);
                // Don't show error for tags as they might not exist
            }
        };
        fetchTags();
    }, [currentWorkspace]);

    // Fetch campaign data if in edit mode
    useEffect(() => {
        if (isEditMode) {
            const fetchCampaign = async () => {
                try {
                    setLoading(true);
                    const headers = currentWorkspace?.id ? { 'x-workspace-id': currentWorkspace.id } : {};
                    const response = await apiClient.get(`/api/email-campaigns/${id}`, { headers });
                    const campaign = response.data.campaign;

                    const scheduleType = campaign.schedule_config?.schedule_type || 'once';
                    const cronExpr = campaign.schedule_config?.cron_expr || '0 9 * * *';

                    // Parse cron for recurring config
                    let recurringConfig = { frequency: 'daily', time: '09:00', daysOfWeek: [], dayOfMonth: 1 };
                    if (cronExpr) {
                        const parts = cronExpr.split(' ');
                        if (parts.length === 5) {
                            const [min, hour, day, month, weekday] = parts;

                            // Convert UTC time from cron expression to local time for display
                            const utcDate = new Date();
                            utcDate.setUTCHours(parseInt(hour), parseInt(min), 0, 0);

                            // Get local time equivalent
                            const localHours = utcDate.getHours();
                            const localMinutes = utcDate.getMinutes();

                            recurringConfig.time = `${localHours.toString().padStart(2, '0')}:${localMinutes.toString().padStart(2, '0')}`;

                            // Determine frequency
                            if (day === '*' && month === '*' && weekday === '*') {
                                recurringConfig.frequency = 'daily';
                            } else if (day === '*' && month === '*') {
                                recurringConfig.frequency = 'weekly';
                                // Parse weekday(s) - could be single or comma-separated
                                if (weekday.includes(',')) {
                                    recurringConfig.daysOfWeek = weekday.split(',').map(d => parseInt(d));
                                } else {
                                    recurringConfig.daysOfWeek = [parseInt(weekday)];
                                }
                            } else if (day !== '*' && month === '*' && weekday === '*') {
                                recurringConfig.frequency = 'monthly';
                                recurringConfig.dayOfMonth = parseInt(day);
                            } else {
                                recurringConfig.frequency = 'custom';
                            }
                        }
                    }

                    // Convert UTC schedule_time to local time for display
                    let scheduleTime = campaign.schedule_config?.schedule_time || '';
                    if (scheduleTime && scheduleType === 'once') {
                        scheduleTime = utcToLocalTime(scheduleTime);
                    }

                    setFormData({
                        name: campaign.name || '',
                        description: campaign.description || '',
                        email_template_id: campaign.email_template_id || '',
                        schedule_type: scheduleType,
                        schedule_time: scheduleTime,
                        cron_expr: cronExpr,
                        recurring_config: recurringConfig,
                        audience: campaign.audience || {
                            type: 'groups',
                            include_groups: []
                        },
                        smtp_id: campaign.smtp_id || '',
                        email_delay_seconds: campaign.email_delay_seconds || 1
                    });
                } catch (error) {
                    console.error('Error fetching campaign:', error);
                    toast.error('Failed to load campaign');
                    navigate('/email-campaigns');
                } finally {
                    setLoading(false);
                }
            };
            fetchCampaign();
        }
    }, [id, isEditMode, currentWorkspace, navigate]);

    // Load template preview when selection changes
    useEffect(() => {
        if (!formData.email_template_id) {
            setTemplatePreview(null);
            return;
        }
        const loadPreview = async () => {
            try {
                setTemplatePreviewLoading(true);
                const headers = currentWorkspace?.id ? { 'x-workspace-id': currentWorkspace.id } : {};
                const response = await apiClient.get(`/api/email-templates/${formData.email_template_id}`, {
                    headers
                });
                setTemplatePreview(response.data.template || null);
            } catch (error) {
                console.error('Error loading template preview:', error);
                setTemplatePreview(null);
            } finally {
                setTemplatePreviewLoading(false);
            }
        };
        loadPreview();
    }, [formData.email_template_id, currentWorkspace]);

    // Calculate total contacts (all vs with email) for selected groups or tags
    useEffect(() => {
        let total = 0;
        let withEmail = 0;
        if (formData.audience.type === 'groups' && formData.audience.include_groups?.length > 0) {
            formData.audience.include_groups.forEach((groupName) => {
                const group = groups.find((g) => g.name === groupName);
                if (group) {
                    total += group.count || 0;
                    withEmail += Number(group.email_count) || 0;
                }
            });
        } else if (formData.audience.type === 'tags' && formData.audience.include_tags?.length > 0) {
            formData.audience.include_tags.forEach((tagName) => {
                const tag = tags.find((t) => t.name === tagName);
                if (tag) {
                    total += tag.count || 0;
                    withEmail += Number(tag.email_count) || 0;
                }
            });
        }
        setTotalContacts(total);
        setTotalContactsWithEmail(withEmail);
    }, [formData.audience, groups, tags]);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.name.trim()) {
            toast.error('Campaign name is required');
            return;
        }

        if (!formData.email_template_id) {
            toast.error('Please select an email template');
            return;
        }

        if (formData.schedule_type === 'once' && !formData.schedule_time) {
            toast.error('Please select a schedule time');
            return;
        }

        if (formData.schedule_type === 'recurring' && !formData.cron_expr) {
            toast.error('Please set up recurring schedule');
            return;
        }

        // Validate target groups or tags
        if (formData.audience.type === 'groups') {
            if (!formData.audience.include_groups || formData.audience.include_groups.length === 0) {
                toast.error('Please select at least one target group');
                return;
            }
        } else if (formData.audience.type === 'tags') {
            if (!formData.audience.include_tags || formData.audience.include_tags.length === 0) {
                toast.error('Please select at least one tag');
                return;
            }
        }

        if (!formData.smtp_id) {
            toast.error('Please select an SMTP configuration');
            return;
        }

        if (totalContactsWithEmail === 0) {
            toast.error(
                'No contacts with a valid email in the selected audience. Add emails to contacts or pick another group/tag.'
            );
            return;
        }

        try {
            setSaving(true);
            const headers = currentWorkspace?.id ? { 'x-workspace-id': currentWorkspace.id } : {};

            // Convert local time to UTC before sending
            const schedule_config = {
                schedule_type: formData.schedule_type,
                ...(formData.schedule_type === 'once' && formData.schedule_time
                    ? { schedule_time: localTimeToUTC(formData.schedule_time) }
                    : {}),
                ...(formData.schedule_type === 'recurring' ? { cron_expr: formData.cron_expr } : {})
            };

            // Log schedule configuration for debugging
            if (schedule_config.schedule_type === 'once' && schedule_config.schedule_time) {
                console.log('📅 Email campaign schedule time conversion (once):', {
                    localTime: formData.schedule_time,
                    utcTime: schedule_config.schedule_time,
                    scheduleType: 'once'
                });
            } else if (schedule_config.schedule_type === 'recurring' && schedule_config.cron_expr) {
                console.log('📅 Email campaign cron schedule (recurring):', {
                    cronExpr: schedule_config.cron_expr,
                    scheduleType: 'recurring',
                    note: 'Cron expressions are always interpreted in UTC timezone by the scheduler'
                });
            }

            console.log('🚀 Creating campaign with data:', {
                name: formData.name,
                email_template_id: formData.email_template_id,
                smtp_id: formData.smtp_id,
                schedule_config: {
                    ...schedule_config,
                    audience: formData.audience
                }
            });

            if (isEditMode) {
                await apiClient.put(`/api/email-campaigns/${id}`, {
                    name: formData.name,
                    description: formData.description,
                    email_template_id: formData.email_template_id,
                    smtp_id: formData.smtp_id,
                    email_delay_seconds: formData.email_delay_seconds,
                    schedule_config: {
                        ...schedule_config,
                        audience: formData.audience
                    }
                }, { headers });

                toast.success('Campaign updated successfully');
            } else {
                const campaignData = {
                    name: formData.name,
                    description: formData.description,
                    email_template_id: formData.email_template_id,
                    smtp_id: formData.smtp_id,
                    email_delay_seconds: formData.email_delay_seconds,
                    trigger_type: 'scheduled',
                    status: 'draft',
                    schedule_config: {
                        ...schedule_config,
                        audience: formData.audience
                    }
                };

                console.log('📤 Sending campaign data:', campaignData);

                await apiClient.post('/api/email-campaigns', campaignData, { headers });

                toast.success('Campaign created successfully');
            }

            // Invalidate and refetch campaigns list to show the new/updated campaign
            await queryClient.invalidateQueries(['emailCampaigns', currentWorkspace?.id]);

            navigate('/email-campaigns');
        } catch (error) {
            console.error('Error saving campaign:', error);
            console.error('Error response:', error.response?.data);
            const errorMessage = error.response?.data?.error || error.response?.data?.errors?.[0]?.msg || `Failed to ${isEditMode ? 'update' : 'create'} campaign`;
            toast.error(errorMessage);
        } finally {
            setSaving(false);
        }
    };



    if (loading) {
        return (
            <div className="flex items-center justify-center h-screen bg-gray-50">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header */}
            <div className="bg-white border-b">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-16">
                        <div className="flex items-center space-x-4">
                            <button
                                onClick={() => navigate('/email-campaigns')}
                                className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                            >
                                <ArrowLeft className="h-5 w-5" />
                            </button>
                            <div>
                                <h1 className="text-xl font-semibold text-gray-900">
                                    {isEditMode ? 'Edit Email Campaign' : 'Create Email Campaign'}
                                </h1>
                                <p className="text-sm text-gray-600">Build automated email sequences</p>
                            </div>
                        </div>
                        <div className="flex items-center space-x-3">
                            <button
                                onClick={() => navigate('/email-campaigns')}
                                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSubmit}
                                disabled={saving}
                                className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                                <Save className="h-4 w-4" />
                                <span>
                                    {saving
                                        ? (isEditMode ? 'Updating...' : 'Creating...')
                                        : (isEditMode ? 'Update Campaign' : 'Create Campaign')
                                    }
                                </span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                <div className="space-y-6">
                    {/* Campaign Configuration */}
                    <div className="bg-white rounded-xl shadow-sm border p-8">
                        <h2 className="text-lg font-medium text-gray-900 flex items-center mb-6">
                            <Settings className="h-5 w-5 mr-2" />
                            Campaign Configuration
                        </h2>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Campaign Name */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Campaign Name *</label>
                                <input
                                    type="text"
                                    value={formData.name}
                                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                    placeholder="e.g., Welcome Email Series"
                                />
                            </div>

                            {/* Email Template Selection */}
                            <div className="md:col-span-2">
                                <label className="block text-sm font-medium text-gray-700 mb-1">Email Template *</label>
                                <select
                                    value={formData.email_template_id}
                                    onChange={(e) => setFormData(prev => ({ ...prev, email_template_id: e.target.value }))}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                >
                                    <option value="">Select an email template</option>
                                    {templates.map(template => (
                                        <option key={template.id} value={template.id}>
                                            {template.name} ({template.template_type})
                                        </option>
                                    ))}
                                </select>

                                {formData.email_template_id && (
                                    <div className="mt-4 border border-gray-200 rounded-lg overflow-hidden bg-gray-50">
                                        <div className="px-4 py-2 border-b border-gray-200 bg-white flex items-center justify-between">
                                            <span className="text-sm font-medium text-gray-800 flex items-center gap-2">
                                                <Mail className="h-4 w-4 text-blue-600" />
                                                Template preview
                                            </span>
                                            {templatePreview?.subject && (
                                                <span className="text-xs text-gray-600 truncate max-w-[60%]">
                                                    Subject: {templatePreview.subject}
                                                </span>
                                            )}
                                        </div>
                                        <div className="p-4 min-h-[120px] max-h-[360px] overflow-auto bg-white">
                                            {templatePreviewLoading ? (
                                                <div className="flex items-center justify-center py-8 text-gray-500 text-sm">
                                                    <Loader2 className="h-5 w-5 animate-spin mr-2" />
                                                    Loading preview…
                                                </div>
                                            ) : templatePreview?.content ? (
                                                templatePreview.template_type === 'html' ||
                                                (templatePreview.type === 'html') ||
                                                /<html[\s>]/i.test(templatePreview.content) ? (
                                                    <iframe
                                                        title="Email template preview"
                                                        srcDoc={templatePreview.content}
                                                        className="w-full min-h-[280px] border-0 rounded"
                                                        sandbox=""
                                                    />
                                                ) : (
                                                    <pre className="text-sm text-gray-800 whitespace-pre-wrap font-sans">
                                                        {templatePreview.content}
                                                    </pre>
                                                )
                                            ) : (
                                                <p className="text-sm text-gray-500 text-center py-6">
                                                    Could not load template content.
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* SMTP Selection */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">SMTP Server *</label>
                                <select
                                    value={formData.smtp_id}
                                    onChange={(e) => setFormData(prev => ({ ...prev, smtp_id: e.target.value }))}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                >
                                    <option value="">Select an SMTP server</option>
                                    {smtpList.map(smtp => (
                                        <option key={smtp.id} value={smtp.id}>
                                            {smtp.name} ({smtp.status === 'active' ? 'Active' : 'Inactive'})
                                        </option>
                                    ))}
                                </select>
                                {formData.smtp_id && (
                                    <p className="text-xs text-gray-500 mt-1">
                                        Emails will be sent using the selected SMTP server
                                    </p>
                                )}
                            </div>

                            {/* Email Delay Between Contacts */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Delay Between Emails (seconds) *</label>
                                <input
                                    type="number"
                                    min="1"
                                    max="3600"
                                    value={formData.email_delay_seconds}
                                    onChange={(e) => setFormData(prev => ({ ...prev, email_delay_seconds: parseInt(e.target.value) || 1 }))}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                />
                                <p className="text-xs text-gray-500 mt-1">
                                    Time to wait between sending each email (1-3600 seconds = up to 60 minutes)
                                </p>
                            </div>

                            {/* Schedule Type */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Schedule Type</label>
                                <select
                                    value={formData.schedule_type}
                                    onChange={(e) => setFormData(prev => ({ ...prev, schedule_type: e.target.value }))}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                >
                                    <option value="once">Run Once</option>
                                    <option value="recurring">Recurring</option>
                                </select>
                            </div>

                            {/* Schedule Time (Once) */}
                            {formData.schedule_type === 'once' && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Schedule Time *</label>
                                    <input
                                        type="datetime-local"
                                        value={formData.schedule_time}
                                        onChange={(e) => setFormData(prev => ({ ...prev, schedule_time: e.target.value }))}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                    />
                                </div>
                            )}

                            {/* Recurring Schedule Configuration */}
                            {formData.schedule_type === 'recurring' && (
                                <div className="md:col-span-2 space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Frequency</label>
                                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                            {['daily', 'weekly', 'monthly', 'custom'].map((freq) => (
                                                <button
                                                    key={freq}
                                                    type="button"
                                                    onClick={() => setFormData(prev => ({
                                                        ...prev,
                                                        recurring_config: { ...prev.recurring_config, frequency: freq },
                                                        cron_expr: freq === 'custom' ? prev.cron_expr : generateCronExpression(freq, prev.recurring_config.time, prev.recurring_config.daysOfWeek, prev.recurring_config.dayOfMonth)
                                                    }))}
                                                    className={`flex flex-col items-center justify-center p-4 rounded-lg border-2 transition-all ${formData.recurring_config.frequency === freq
                                                        ? 'border-blue-500 bg-blue-50'
                                                        : 'border-gray-200 hover:border-gray-300'
                                                        }`}
                                                >
                                                    <div className="mb-2">
                                                        {freq === 'daily' && <Calendar className="h-5 w-5" />}
                                                        {freq === 'weekly' && <Calendar className="h-5 w-5" />}
                                                        {freq === 'monthly' && <Calendar className="h-5 w-5" />}
                                                        {freq === 'custom' && <Settings className="h-5 w-5" />}
                                                    </div>
                                                    <span className="text-sm font-medium capitalize">{freq}</span>
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Time</label>
                                            <div className="relative">
                                                <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                                                <input
                                                    type="time"
                                                    value={formData.recurring_config.time}
                                                    onChange={(e) => {
                                                        const newTime = e.target.value;
                                                        setFormData(prev => ({
                                                            ...prev,
                                                            recurring_config: { ...prev.recurring_config, time: newTime },
                                                            cron_expr: prev.recurring_config.frequency !== 'custom'
                                                                ? generateCronExpression(prev.recurring_config.frequency, newTime, prev.recurring_config.daysOfWeek, prev.recurring_config.dayOfMonth)
                                                                : prev.cron_expr
                                                        }));
                                                    }}
                                                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                                />
                                            </div>
                                        </div>

                                        {/* Schedule Preview */}
                                        <div className="md:col-span-2">
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Schedule Preview</label>
                                            <div className="bg-gray-50 p-3 rounded-lg border border-gray-200 flex items-center gap-2 h-[42px]">
                                                <code className="px-2 py-1 bg-white border border-gray-300 rounded text-xs font-mono">
                                                    {formData.cron_expr || '0 9 * * *'}
                                                </code>
                                                <span className="text-xs text-gray-600">
                                                    {formatCronToReadable(formData.cron_expr || '0 9 * * *')}
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Days of Week - For Weekly Schedule */}
                                    {formData.recurring_config.frequency === 'weekly' && (
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Days of Week</label>
                                            <div className="grid grid-cols-7 gap-2">
                                                {[
                                                    { value: 0, label: 'Sun' },
                                                    { value: 1, label: 'Mon' },
                                                    { value: 2, label: 'Tue' },
                                                    { value: 3, label: 'Wed' },
                                                    { value: 4, label: 'Thu' },
                                                    { value: 5, label: 'Fri' },
                                                    { value: 6, label: 'Sat' }
                                                ].map((day) => {
                                                    const isSelected = formData.recurring_config.daysOfWeek.includes(day.value);
                                                    return (
                                                        <button
                                                            key={day.value}
                                                            type="button"
                                                            onClick={() => {
                                                                const newDaysOfWeek = isSelected
                                                                    ? formData.recurring_config.daysOfWeek.filter(d => d !== day.value)
                                                                    : [...formData.recurring_config.daysOfWeek, day.value];
                                                                const updatedConfig = { ...formData.recurring_config, daysOfWeek: newDaysOfWeek };
                                                                setFormData(prev => ({
                                                                    ...prev,
                                                                    recurring_config: updatedConfig,
                                                                    cron_expr: generateCronExpression(prev.recurring_config.frequency, prev.recurring_config.time, newDaysOfWeek, prev.recurring_config.dayOfMonth)
                                                                }));
                                                            }}
                                                            className={`p-2 rounded-lg border-2 font-medium text-sm transition-all ${isSelected
                                                                ? 'bg-blue-500 text-white border-blue-600'
                                                                : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400'
                                                                }`}
                                                        >
                                                            {day.label}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                            {formData.recurring_config.daysOfWeek.length === 0 && (
                                                <p className="text-xs text-red-500 mt-1">Please select at least one day</p>
                                            )}
                                        </div>
                                    )}

                                    {/* Day of Month - For Monthly Schedule */}
                                    {formData.recurring_config.frequency === 'monthly' && (
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Day of Month</label>
                                            <div className="relative">
                                                <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                                                <select
                                                    value={formData.recurring_config.dayOfMonth}
                                                    onChange={(e) => {
                                                        const newDayOfMonth = parseInt(e.target.value);
                                                        setFormData(prev => ({
                                                            ...prev,
                                                            recurring_config: { ...prev.recurring_config, dayOfMonth: newDayOfMonth },
                                                            cron_expr: generateCronExpression(prev.recurring_config.frequency, prev.recurring_config.time, prev.recurring_config.daysOfWeek, newDayOfMonth)
                                                        }));
                                                    }}
                                                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm appearance-none bg-white"
                                                >
                                                    {Array.from({ length: 31 }, (_, i) => i + 1).map(day => (
                                                        <option key={day} value={day}>{day}</option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>
                                    )}

                                    {formData.recurring_config.frequency === 'custom' && (
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Cron Expression *</label>
                                            <input
                                                type="text"
                                                value={formData.cron_expr}
                                                onChange={(e) => setFormData(prev => ({ ...prev, cron_expr: e.target.value }))}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm font-mono"
                                                placeholder="0 9 * * *"
                                            />
                                            <p className="text-xs text-gray-500 mt-1">Format: minute hour day month weekday</p>
                                        </div>
                                    )}

                                </div>
                            )}

                            {/* Target Audience - Groups or Tags */}
                            <div className="md:col-span-2">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Target Audience Type *
                                        </label>
                                        <select
                                            value={formData.audience.type}
                                            onChange={(e) => {
                                                setFormData({
                                                    ...formData,
                                                    audience: {
                                                        type: e.target.value,
                                                        include_groups: [],
                                                        include_tags: []
                                                    }
                                                });
                                            }}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                        >
                                            <option value="groups">Select by Groups</option>
                                            <option value="tags">Select by Tags</option>
                                        </select>
                                    </div>

                                    {/* Groups Selection */}
                                    {formData.audience.type === 'groups' && (
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Select Groups
                                                <span className="text-xs text-gray-500 ml-1">(Choose one or more)</span>
                                            </label>
                                            <div className="space-y-2 max-h-40 overflow-y-auto border border-gray-200 rounded-lg p-4 bg-gray-50">
                                                {!Array.isArray(groups) || groups.length === 0 ? (
                                                    <p className="text-sm text-gray-500 text-center py-4">No groups available</p>
                                                ) : (
                                                    groups.map((group) => {
                                                        const isSelected = formData.audience.include_groups.includes(group.name);
                                                        const emailCount = Number(group.email_count) || 0;
                                                        const totalInGroup = Number(group.count) || 0;
                                                        const noEmail = emailCount === 0;
                                                        return (
                                                            <label
                                                                key={group.name}
                                                                className={`flex items-center space-x-3 p-3 rounded-lg border-2 transition-all duration-200 ${noEmail
                                                                    ? 'bg-gray-100 border-gray-200 opacity-60 cursor-not-allowed'
                                                                    : isSelected
                                                                        ? 'bg-blue-50 border-blue-300 shadow-sm cursor-pointer'
                                                                        : 'bg-white border-gray-200 hover:bg-gray-50 hover:border-gray-300 cursor-pointer'
                                                                    }`}
                                                            >
                                                                <div className="relative">
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={isSelected}
                                                                        disabled={noEmail}
                                                                        onChange={(e) => {
                                                                            if (noEmail) return;
                                                                            const include_groups = e.target.checked
                                                                                ? [...formData.audience.include_groups, group.name]
                                                                                : formData.audience.include_groups.filter(g => g !== group.name);
                                                                            setFormData({ ...formData, audience: { ...formData.audience, include_groups } });
                                                                        }}
                                                                        className="w-5 h-5 rounded border-2 border-gray-300 text-blue-600 focus:ring-blue-500 focus:ring-2 disabled:opacity-50"
                                                                    />
                                                                    {isSelected && (
                                                                        <div className="absolute inset-0 flex items-center justify-center">
                                                                            <svg className="w-3 h-3 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                                                                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                                            </svg>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                                <div className="flex-1">
                                                                    <span className={`text-sm font-medium ${isSelected ? 'text-blue-900' : 'text-gray-700'}`}>
                                                                        {group.name}
                                                                    </span>
                                                                    <span className={`text-xs ml-2 ${isSelected ? 'text-blue-600' : 'text-gray-500'}`}>
                                                                        {totalInGroup} contact{totalInGroup !== 1 ? 's' : ''}
                                                                        {emailCount > 0 ? (
                                                                            <span className="text-green-700 font-medium">
                                                                                {' '}
                                                                                · {emailCount} will receive email
                                                                            </span>
                                                                        ) : null}
                                                                        {totalInGroup > emailCount && emailCount > 0 ? (
                                                                            <span className="text-gray-500">
                                                                                {' '}
                                                                                ({totalInGroup - emailCount} without email, skipped)
                                                                            </span>
                                                                        ) : null}
                                                                    </span>
                                                                    {noEmail && (
                                                                        <span className="block text-xs text-amber-700 mt-0.5">
                                                                            No valid email in this group — add emails to contacts first
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </label>
                                                        );
                                                    })
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    {/* Tags Selection */}
                                    {formData.audience.type === 'tags' && (
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Select Tags
                                                <span className="text-xs text-gray-500 ml-1">(Choose one or more)</span>
                                            </label>
                                            <div className="space-y-2 max-h-40 overflow-y-auto border border-gray-200 rounded-lg p-4 bg-gray-50">
                                                {!Array.isArray(tags) || tags.length === 0 ? (
                                                    <p className="text-sm text-gray-500 text-center py-4">No tags available</p>
                                                ) : (
                                                    tags.map((tag) => {
                                                        const isSelected = formData.audience.include_tags.includes(tag.name);
                                                        const emailCount = Number(tag.email_count) || 0;
                                                        const totalInTag = Number(tag.count) || 0;
                                                        const noEmail = emailCount === 0;
                                                        return (
                                                            <label
                                                                key={tag.name}
                                                                className={`flex items-center space-x-3 p-3 rounded-lg border-2 transition-all duration-200 ${noEmail
                                                                    ? 'bg-gray-100 border-gray-200 opacity-60 cursor-not-allowed'
                                                                    : isSelected
                                                                        ? 'bg-blue-50 border-blue-300 shadow-sm cursor-pointer'
                                                                        : 'bg-white border-gray-200 hover:bg-gray-50 hover:border-gray-300 cursor-pointer'
                                                                    }`}
                                                            >
                                                                <div className="relative">
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={isSelected}
                                                                        disabled={noEmail}
                                                                        onChange={(e) => {
                                                                            if (noEmail) return;
                                                                            const include_tags = e.target.checked
                                                                                ? [...formData.audience.include_tags, tag.name]
                                                                                : formData.audience.include_tags.filter(t => t !== tag.name);
                                                                            setFormData({ ...formData, audience: { ...formData.audience, include_tags } });
                                                                        }}
                                                                        className="w-5 h-5 rounded border-2 border-gray-300 text-blue-600 focus:ring-blue-500 focus:ring-2 disabled:opacity-50"
                                                                    />
                                                                    {isSelected && (
                                                                        <div className="absolute inset-0 flex items-center justify-center">
                                                                            <svg className="w-3 h-3 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                                                                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                                            </svg>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                                <div className="flex-1">
                                                                    <span className={`text-sm font-medium ${isSelected ? 'text-blue-900' : 'text-gray-700'}`}>
                                                                        {tag.name}
                                                                    </span>
                                                                    <span className={`text-xs ml-2 ${isSelected ? 'text-blue-600' : 'text-gray-500'}`}>
                                                                        {totalInTag} contact{totalInTag !== 1 ? 's' : ''}
                                                                        {emailCount > 0 ? (
                                                                            <span className="text-green-700 font-medium">
                                                                                {' '}
                                                                                · {emailCount} will receive email
                                                                            </span>
                                                                        ) : null}
                                                                        {totalInTag > emailCount && emailCount > 0 ? (
                                                                            <span className="text-gray-500">
                                                                                {' '}
                                                                                ({totalInTag - emailCount} without email, skipped)
                                                                            </span>
                                                                        ) : null}
                                                                    </span>
                                                                    {noEmail && (
                                                                        <span className="block text-xs text-amber-700 mt-0.5">
                                                                            No valid email in this tag — add emails to contacts first
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </label>
                                                        );
                                                    })
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Selection Summary */}
                                {((formData.audience.type === 'groups' && formData.audience.include_groups.length > 0) ||
                                    (formData.audience.type === 'tags' && formData.audience.include_tags.length > 0)) && (
                                        <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                                            <div className="flex items-center">
                                                <svg className="w-4 h-4 text-green-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                                </svg>
                                                <span className="text-sm font-medium text-green-800">
                                                    {formData.audience.type === 'groups'
                                                        ? `${formData.audience.include_groups.length} group(s) selected`
                                                        : `${formData.audience.include_tags.length} tag(s) selected`
                                                    }
                                                </span>
                                            </div>
                                            <p className="text-xs text-green-600 mt-1">
                                                <strong>{totalContactsWithEmail}</strong> contact
                                                {totalContactsWithEmail !== 1 ? 's' : ''} will receive this campaign
                                                {totalContacts > totalContactsWithEmail && (
                                                    <span className="text-gray-500 block mt-0.5">
                                                        {totalContacts - totalContactsWithEmail} other contact
                                                        {totalContacts - totalContactsWithEmail !== 1 ? 's' : ''} in selected
                                                        groups/tags have no valid email and are skipped automatically.
                                                    </span>
                                                )}
                                            </p>
                                            {totalContactsWithEmail === 0 && (
                                                <p className="text-xs text-red-600 mt-1 font-medium">
                                                    Select at least one group/tag that has contacts with email addresses.
                                                </p>
                                            )}
                                            {/* SMTP Limit Warning */}
                                            {formData.smtp_id && (() => {
                                                const selectedSmtp = smtpList.find(s => s.id === parseInt(formData.smtp_id));
                                                const smtpLimit = selectedSmtp?.daily_email_limit || 0;
                                                if (totalContactsWithEmail > smtpLimit) {
                                                    return (
                                                        <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded">
                                                            <div className="flex items-start">
                                                                <svg className="w-4 h-4 text-red-600 mr-2 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                                                                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                                                </svg>
                                                                <div>
                                                                    <p className="text-xs font-medium text-red-800">
                                                                        Warning: Email recipients ({totalContactsWithEmail}) exceeds SMTP daily limit ({smtpLimit})
                                                                    </p>
                                                                    <p className="text-xs text-red-600 mt-1">
                                                                        Campaign will fail in the middle. Please reduce contacts or increase SMTP limit.
                                                                    </p>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );
                                                }
                                                return null;
                                            })()}
                                        </div>
                                    )}

                                {/* No Selection Warning */}
                                {((formData.audience.type === 'groups' && formData.audience.include_groups.length === 0) ||
                                    (formData.audience.type === 'tags' && formData.audience.include_tags.length === 0)) && (
                                        <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                                            <div className="flex items-center">
                                                <svg className="w-4 h-4 text-yellow-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                                </svg>
                                                <span className="text-sm font-medium text-yellow-800">
                                                    No {formData.audience.type} selected
                                                </span>
                                            </div>
                                            <p className="text-xs text-yellow-600 mt-1">
                                                Please select at least one group to continue
                                            </p>
                                        </div>
                                    )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CreateEmailCampaign;
